import type { CSSProperties } from 'react'
import type { Theme } from '@mui/material'

/**
 * ── THE HOUSE CHART STYLE, IN ONE PLACE ─────────────────────────────────────────────
 *
 * Every recharts chart in the platform draws with the same small set of decisions: one
 * `brand[500]` series, a monotone 2px area over a 0.3 -> 0 gradient of its own colour,
 * solid round-topped bars, a recessive dashed horizontal grid, line-less axes with
 * `text.secondary` ticks, and a paper tooltip. Those values were set by
 * `BillingOverview` and then hand-copied into `ScansByTime2` and `RoiValueChart` (their
 * comments say "copied from BillingOverview exactly"). The internal analytics page was
 * about to be the fourth copy, which is past the point §36 of the working conventions
 * says to extract, so this module is the copy the others read from.
 *
 * Everything is a theme token. The `brand` ramp is mode-independent (see
 * `packages/ui/theme/brandText.ts`), so a series is the same purple in light and dark,
 * while the grid, ticks, tooltip surface and cursor are all mode-aware palette entries.
 * A chart that reads from here therefore has light/dark parity with `/analytics` by
 * construction rather than by matching it by eye.
 *
 * Deliberately NOT here: anything a chart decides for itself (margins, whether the
 * y-axis shows, tick density, data-dependent gradient ids). Those differ between charts
 * for stated reasons in each file.
 */

/** One size for both axes' tick labels, so the two can never drift apart. */
export const CHART_AXIS_TICK_FONT_SIZE = 11

/**
 * The series colour for a single-series chart. Not a slot in a categorical rotation: a
 * chart with one series draws it in the brand colour, the way billing and `/analytics`
 * draw their totals.
 */
export const chartSeriesColor = (theme: Theme): string => theme.palette.brand[500]

/** The area fill's gradient: 0.3 at the line, fading to 0 at the baseline. */
export const CHART_AREA_GRADIENT_OPACITY = { top: 0.3, bottom: 0 } as const

/**
 * The `<linearGradient>` under an area. Render it inside the chart's `<defs>` (recharts
 * passes SVG elements such as `defs` through untouched, so a component inside one renders
 * normally; directly under the chart it would be dropped). The `id` is the caller's: it has
 * to be unique on the page, and only the caller knows what makes it so.
 */
export const ChartAreaGradient = ({ id, color }: { id: string; color: string }) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor={color} stopOpacity={CHART_AREA_GRADIENT_OPACITY.top} />
    <stop offset="100%" stopColor={color} stopOpacity={CHART_AREA_GRADIENT_OPACITY.bottom} />
  </linearGradient>
)

/** `<Area>`'s fixed props: monotone, 2px, no resting dots. Spread before per-chart props. */
export const CHART_AREA_PROPS = {
  type: 'monotone',
  strokeWidth: 2,
  dot: false,
} as const

/**
 * The hovered point: a surface-coloured ring in the series colour. Paper, not white, so it
 * is not a bright hole punched in the line on the dark theme.
 */
export const chartActiveDot = (theme: Theme, color: string) => ({
  r: 4,
  stroke: color,
  strokeWidth: 2,
  fill: theme.palette.background.paper,
})

/**
 * `<Bar>`'s fixed props. Fill is SOLID (pass the series colour as `fill`), never the area's
 * gradient: a bar's length is read against the baseline, and a fade toward the baseline
 * softens exactly the edge the value is read from. Animation is off because recharts draws
 * no bar rectangles at all until `requestAnimationFrame` first ticks, so a throttled rAF
 * (a backgrounded tab, a print path, the preview pane) renders an empty chart.
 */
export const CHART_BAR_PROPS = {
  radius: [4, 4, 0, 0] as [number, number, number, number],
  maxBarSize: 40,
  isAnimationActive: false,
}

/** `<CartesianGrid>`: horizontal rules only, recessive and dashed. */
export const chartGridProps = (theme: Theme) => ({
  vertical: false,
  stroke: theme.palette.primary[100],
  strokeDasharray: '4 4',
})

/** `<XAxis>` / `<YAxis>`: no axis line, no tick lines. The grid does that work. */
export const CHART_AXIS_PROPS = {
  axisLine: false,
  tickLine: false,
} as const

/** An axis' `tick` prop. */
export const chartAxisTick = (theme: Theme) => ({
  fontSize: CHART_AXIS_TICK_FONT_SIZE,
  fill: theme.palette.text.secondary,
})

/** `<Tooltip>`'s box, label and item styles. Spread onto the tooltip. */
export const chartTooltipStyle = (
  theme: Theme,
): { contentStyle: CSSProperties; labelStyle: CSSProperties; itemStyle: CSSProperties } => ({
  contentStyle: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    // '8px', NOT 8. This object is used two ways: recharts reads it as inline CSS
    // (where 8 means 8px) and SankeyChart spreads it into an MUI `sx` (where 8 is a
    // MULTIPLIER of shape.borderRadius: 4, so it rendered at 32px — four times the
    // 8px every other tooltip uses). A unit string is correct in both.
    borderRadius: '8px',
    boxShadow: theme.shadows[2],
  },
  labelStyle: { color: theme.palette.text.secondary },
  itemStyle: { color: theme.palette.text.primary },
})

/**
 * `<Tooltip>`'s cursor, BillingOverview's pairing: a hovered BAR wants its column washed, a
 * hovered point on a line wants a vertical rule to read against the axis.
 */
export const chartTooltipCursor = (theme: Theme, mark: 'bar' | 'line') =>
  mark === 'bar' ? { fill: theme.palette.action.hover } : { stroke: theme.palette.grey[300] }
